import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  mentoringConfig,
  minimumWalletTopUp,
  sortedPlans,
  trackerConfig,
  ui,
} from './constants';
import { usePricingTariffs } from './context/PricingTariffsContext';
import { MENTORING_PLAN_KEY } from './utils/addonPricing';
import {
  FREE_PLAN_ID,
  FREE_PLAN_PERIOD_DAYS,
  FREE_PLAN_TRACKER_KEYWORDS,
  FREE_PLAN_TRACKER_FREQUENCY,
  FREE_PLAN_TRACKER_DEPTH,
  getPlanPrice,
  hasFreePlanPrice,
  isFreePlan,
} from './utils/tariffPricing';
import { normalizeWalletAmount, smoothScrollToElement } from './utils';
import MentoringCard from './components/MentoringCard';
import NoPlanOption from './components/NoPlanOption';
import PeriodSelector from './components/PeriodSelector';
import PlanCard from './components/PlanCard';
import Summary from './components/Summary';
import ToolsComparisonCard from './components/ToolsComparisonCard';
import TrackerCard from './components/TrackerCard';
import WalletTopUpCard from './components/WalletTopUpCard';
import { useMembershipWizard } from '@/app/Membership/context/MembershipWizardContext';
import type {
  NewPricingProps,
  PeriodDays,
  PlanId,
  TrackerFrequencyId,
  TrackerResultDepth,
} from './types';

function parseWalletChargeAmount(raw: string | null): number | null {
  if (!raw) return null;
  const amount = Number.parseInt(raw, 10);
  if (!Number.isFinite(amount) || amount < minimumWalletTopUp) return null;
  return normalizeWalletAmount(amount);
}

export default function NewPricing({
  setSelectedTariff,
  isMemberStep = false,
}: NewPricingProps) {
  const { plans } = usePricingTariffs();
  const membershipWizard = useMembershipWizard();
  const [searchParams] = useSearchParams();
  const walletChargeAmount = useMemo(
    () => parseWalletChargeAmount(searchParams.get('wallet')),
    [searchParams],
  );
  const [selectedPlan, setSelectedPlan] = useState<PlanId | null>(null);
  const [period, setPeriod] = useState<PeriodDays>(30);
  const [trackerEnabled, setTrackerEnabled] = useState(false);
  const [keywords, setKeywords] = useState(trackerConfig.keywordRange.min);
  const [trackerFrequency, setTrackerFrequency] = useState<TrackerFrequencyId>(
    trackerConfig.defaultFrequencyId,
  );
  const [trackerResultDepth, setTrackerResultDepth] = useState<TrackerResultDepth>(
    trackerConfig.defaultResultDepth,
  );
  const [mentoringEnabled, setMentoringEnabled] = useState(false);
  const [walletTopUpEnabled, setWalletTopUpEnabled] = useState(false);
  const [walletTopUpAmount, setWalletTopUpAmount] = useState(minimumWalletTopUp);
  const addonsGroupRef = useRef<HTMLDivElement>(null);
  const appliedWalletChargeRef = useRef(false);

  const visiblePlans = useMemo(
    () =>
      sortedPlans.filter((plan) => {
        const planId = plan.id as PlanId;

        if (planId === FREE_PLAN_ID) {
          return isMemberStep && hasFreePlanPrice(plans);
        }

        return Boolean(plans[planId]);
      }),
    [isMemberStep, plans],
  );

  const isFreePlanSelected = isFreePlan(selectedPlan);
  const effectiveTrackerEnabled = isFreePlanSelected || trackerEnabled;
  const effectiveMentoringEnabled =
    !isFreePlanSelected && mentoringEnabled && !mentoringConfig.disabled;
  const effectiveKeywords = isFreePlanSelected
    ? FREE_PLAN_TRACKER_KEYWORDS
    : keywords;
  const effectiveTrackerFrequency = isFreePlanSelected
    ? FREE_PLAN_TRACKER_FREQUENCY
    : trackerFrequency;
  const effectiveTrackerResultDepth = isFreePlanSelected
    ? FREE_PLAN_TRACKER_DEPTH
    : trackerResultDepth;

  useEffect(() => {
    const visiblePlanIds = visiblePlans.map((plan) => plan.id as PlanId);

    if (!visiblePlanIds.length) {
      setSelectedPlan(null);
      return;
    }

    setSelectedPlan((current) => {
      if (current && visiblePlanIds.includes(current)) return current;

      // Wallet charge from modal: keep "بدون طرح اصلی"
      if (walletChargeAmount != null) return null;

      if (isMemberStep && visiblePlanIds.includes(FREE_PLAN_ID)) {
        return FREE_PLAN_ID;
      }

      return visiblePlanIds[0] ?? null;
    });
  }, [isMemberStep, visiblePlans, walletChargeAmount]);

  useEffect(() => {
    if (isMemberStep || walletChargeAmount == null || appliedWalletChargeRef.current) {
      return;
    }

    appliedWalletChargeRef.current = true;
    setSelectedPlan(null);
    setWalletTopUpEnabled(true);
    setWalletTopUpAmount(normalizeWalletAmount(walletChargeAmount));

    const frameId = window.requestAnimationFrame(() => {
      if (addonsGroupRef.current) {
        smoothScrollToElement(addonsGroupRef.current, { duration: 1000, offset: 24 });
      }
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [isMemberStep, walletChargeAmount]);

  useEffect(() => {
    if (!isFreePlanSelected) return;

    setPeriod(FREE_PLAN_PERIOD_DAYS);
    setTrackerEnabled(true);
    setKeywords(FREE_PLAN_TRACKER_KEYWORDS);
    setTrackerFrequency(FREE_PLAN_TRACKER_FREQUENCY);
    setTrackerResultDepth(FREE_PLAN_TRACKER_DEPTH);
    setMentoringEnabled(false);
  }, [isFreePlanSelected]);

  useEffect(() => {
    if (!isMemberStep || !membershipWizard) return;

    membershipWizard.setSkipPaymentStep(isFreePlanSelected);
    membershipWizard.setSkipKeywordsStep(!isFreePlanSelected && !trackerEnabled);
  }, [isMemberStep, isFreePlanSelected, trackerEnabled, membershipWizard]);

  const selectedPrice = useMemo(
    () => getPlanPrice(plans, selectedPlan, period),
    [plans, selectedPlan, period],
  );

  useEffect(() => {
    if (typeof setSelectedTariff !== 'function') return;

    if (!selectedPlan || !selectedPrice) {
      setSelectedTariff({ price_slug: '', weekly_updates: 3 });
      return;
    }

    setSelectedTariff({
      price_slug: selectedPrice.id,
      weekly_updates: 3,
      period,
      plan_id: selectedPlan,
      id: selectedPrice.id,
      price: selectedPrice.price,
    });
  }, [selectedPlan, selectedPrice, period, setSelectedTariff]);

  function handlePlanSelect(planId: PlanId) {
    setSelectedPlan((current) => {
      const next: PlanId | null = current === planId ? null : planId;

      if (next === FREE_PLAN_ID) {
        setPeriod(FREE_PLAN_PERIOD_DAYS);
        setTrackerEnabled(true);
        setKeywords(FREE_PLAN_TRACKER_KEYWORDS);
        setTrackerFrequency(FREE_PLAN_TRACKER_FREQUENCY);
        setTrackerResultDepth(FREE_PLAN_TRACKER_DEPTH);
        setMentoringEnabled(false);
      } else if (isFreePlan(current)) {
        setPeriod(30);
        setTrackerEnabled(false);
        setKeywords(trackerConfig.keywordRange.min);
        setTrackerFrequency(trackerConfig.defaultFrequencyId);
        setTrackerResultDepth(trackerConfig.defaultResultDepth);
      }

      return next;
    });
  }

  function handleNoPlanSelect() {
    const wasAlreadyNoPlan = !selectedPlan;

    setSelectedPlan(null);
    if (period === FREE_PLAN_PERIOD_DAYS) {
      setPeriod(30);
    }

    if (!wasAlreadyNoPlan && addonsGroupRef.current) {
      smoothScrollToElement(addonsGroupRef.current, { duration: 1000, offset: 24 });
    }
  }

  function toggleTracker(checked: boolean) {
    if (isFreePlanSelected || trackerConfig.disabled) return;

    setTrackerEnabled(checked);
    if (checked) setPeriod(30);
  }

  function toggleMentoring(checked: boolean) {
    if (isFreePlanSelected || mentoringConfig.disabled) return;

    setMentoringEnabled(checked);
    if (checked) setPeriod(30);
  }

  function updateKeywords(value: string | number) {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) return;
    const steppedValue =
      Math.round(parsed / trackerConfig.keywordRange.step) *
      trackerConfig.keywordRange.step;
    setKeywords(
      Math.min(
        trackerConfig.keywordRange.max,
        Math.max(trackerConfig.keywordRange.min, steppedValue),
      ),
    );
  }

  function updateWalletTopUpAmount(value: string | number) {
    setWalletTopUpAmount(normalizeWalletAmount(value));
  }

  return (
    <main className="new-pricing text-900">
      <div className="ambient ambient-one rounded-circle" />
      <div className="ambient ambient-two rounded-circle" />
      <div className="content-grid">
        <div className="configurator">
          <div className="stage-group">
            <div className="stage-heading stage-heading-row">
              <div>
                <span className="eyebrow fs--2">{ui.stages.plans.eyebrow}</span>
                <h2 className="text-1000">{ui.stages.plans.title}</h2>
              </div>
              <span className="current-price">
                {selectedPrice ? (
                  <>
                    {ui.stages.plans.startFromPrefix}{' '}
                    <strong className="text-900">{selectedPrice.finalLabel}</strong>
                  </>
                ) : (
                  ui.stages.plans.noPlanPriceLabel
                )}
              </span>
            </div>

            <section className="plans-panel rounded-4 bg-white dark__bg-card-dark">
              <div className="plans-grid">
                {visiblePlans.map((plan) => {
                  const planId = plan.id as PlanId;

                  return (
                    <PlanCard
                      key={planId}
                      plan={plans[planId]}
                      selected={selectedPlan === planId}
                      onSelect={() => handlePlanSelect(planId)}
                    />
                  );
                })}

                <NoPlanOption
                  selected={!selectedPlan}
                  onSelect={handleNoPlanSelect}
                />
              </div>
            </section>

            <ToolsComparisonCard />
          </div>

          <div className="addons-group" ref={addonsGroupRef}>
            <div className="addons-heading">
              <span className="eyebrow fs--2">{ui.stages.addons.eyebrow}</span>
              <h2 className="text-1000">{ui.stages.addons.title}</h2>
            </div>

            {isFreePlanSelected ? (
              <TrackerCard
                locked
                enabled
                onToggle={() => undefined}
                keywords={effectiveKeywords}
                onKeywordsChange={() => undefined}
                frequency={effectiveTrackerFrequency}
                onFrequencyChange={() => undefined}
                resultDepth={effectiveTrackerResultDepth}
                onResultDepthChange={() => undefined}
              />
            ) : (
              <>
                <TrackerCard
                  enabled={trackerEnabled}
                  onToggle={toggleTracker}
                  keywords={keywords}
                  onKeywordsChange={updateKeywords}
                  frequency={trackerFrequency}
                  onFrequencyChange={setTrackerFrequency}
                  resultDepth={trackerResultDepth}
                  onResultDepthChange={setTrackerResultDepth}
                />

                {!isMemberStep ? (
                  <WalletTopUpCard
                    enabled={walletTopUpEnabled}
                    onToggle={setWalletTopUpEnabled}
                    amount={walletTopUpAmount}
                    onAmountChange={updateWalletTopUpAmount}
                  />
                ) : null}

                <MentoringCard
                  enabled={mentoringEnabled}
                  onToggle={toggleMentoring}
                />
              </>
            )}
          </div>

          {!isFreePlanSelected ? (
            <PeriodSelector
              value={period}
              onChange={setPeriod}
              disabled={trackerEnabled || effectiveMentoringEnabled}
              walletTopUpEnabled={!isMemberStep && walletTopUpEnabled}
              restrictionLabel={
                trackerEnabled && effectiveMentoringEnabled
                  ? `${trackerConfig.name} و ${mentoringConfig.name}`
                  : trackerEnabled
                    ? trackerConfig.name
                    : mentoringConfig.name
              }
            />
          ) : null}
        </div>

        <div className="summary-column">
          <Summary
            selectedPlan={selectedPlan}
            period={period}
            trackerEnabled={effectiveTrackerEnabled}
            keywords={effectiveKeywords}
            frequency={effectiveTrackerFrequency}
            resultDepth={effectiveTrackerResultDepth}
            mentoringEnabled={effectiveMentoringEnabled}
            mentoringPlanKey={MENTORING_PLAN_KEY}
            walletTopUpEnabled={!isMemberStep && walletTopUpEnabled}
            walletTopUpAmount={walletTopUpAmount}
            isMemberStep={isMemberStep}
          />
        </div>
      </div>
    </main>
  );
}
